import { z } from 'zod';
import { approvalSummarySchema } from '../../approvals/schemas/approvals.schemas.js';
import { acquisitionTypeSchema, activityKindSchema, estimateStatusSchema, fuelSchema, grade7Schema, inventoryStatusSchema, originSchema, profitModeSchema, quickCategorySchema, serviceHistorySchema, severitySchema, usageSchema, } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
import { amountSchema, isoDateSchema, revisionSchema, signedMoneySchema } from '../../common/schemas/money.schemas.js';
import { paginatedSchema, paginationInputSchema, sortDirectionSchema } from '../../common/schemas/pagination.schemas.js';
import { costItemsSummarySchema } from '../../costs/schemas/costs.schemas.js';
/**
 * An **estimate is a customer opportunity, never stock** (plan §3.1). It carries the customer, the
 * vehicle facts, the template version it is priced with, the inspection, the quick range and — from
 * phase 4 — the offer and its outcome. It is numbered `EST-<year>-NNNN` per tenant and stays searchable
 * for ever, whether or not it became a purchase.
 *
 * Two rules shape every schema here:
 *
 *  - **Unknown is a state of its own.** A condition that nobody has recorded is `UNKNOWN`, never
 *    `EXCELLENT`, and a facelift/customs question nobody answered is `null`, never `false` (PP AC03).
 *  - **Percentages are fractions on the wire.** Only the UI ever prints a `%` (plan §11.6).
 */
/** The odometer is whole kilometres; nothing in the workshop measures a fraction of one. */
export const odometerSchema = z.number().int().min(0, 'The odometer reading cannot be negative').max(9_999_999);
export const ownerCountSchema = z.number().int().min(0, 'The number of owners cannot be negative').max(99);
export const modelYearSchema = z.number().int().min(1900).max(2999);
/** A VIN is 17 characters (ISO 3779); a chassis number from an older vehicle is shorter, never longer. */
export const VIN_MAX_LENGTH = 17;
/**
 * VIN / chassis as the salesperson typed it; the platform keeps a normalised copy for matching. The
 * 17-character limit is the business review's (2026-09-23): the input stops at 17, and this schema
 * refuses anything longer that reaches the service another way. The web prints its own translated
 * sentence (`estimates.vinTooLong`); this message is the one a `curl` caller reads.
 */
export const vinSchema = z.string().trim().max(VIN_MAX_LENGTH, `A VIN has at most ${VIN_MAX_LENGTH} characters`);
/**
 * One per-estimate override of a template value (plan §4 `param_overrides`). `value` is the number the
 * equations read; `reason` is what the salesperson typed; `byUserId` and `at` are stamped by the
 * service, never accepted from the client.
 */
export const paramOverrideSchema = z.object({
    value: z.number().finite(),
    reason: z.string().trim().max(500).nullable(),
    byUserId: uuidSchema.nullable(),
    at: z.string().datetime().nullable(),
});
export const paramOverridesSchema = z.record(z.string(), paramOverrideSchema);
/** What an update may send: the value and the reason. The rest is stamped server-side. */
export const paramOverrideInputSchema = z.object({
    /** `null` removes the override and the template value applies again. */
    value: z.number().finite().nullable(),
    reason: z.string().trim().max(500).optional(),
});
export const paramOverridesInputSchema = z.record(z.string(), paramOverrideInputSchema);
/**
 * The shared vehicle-fact columns of plan §4 — one shape for the estimate today and the stock vehicle
 * of phase 4, so the purchase copies them 1:1.
 */
export const vehicleFactsSchema = z.object({
    templateId: uuidSchema.nullable(),
    templateVersionId: uuidSchema.nullable(),
    policyVersionId: uuidSchema.nullable(),
    make: z.string().nullable(),
    model: z.string().nullable(),
    trim: z.string().nullable(),
    modelYear: z.number().int().nullable(),
    vin: z.string().nullable(),
    vinNormalized: z.string().nullable(),
    plate: z.string().nullable(),
    drivetrain: z.string().nullable(),
    transmission: z.string().nullable(),
    fuel: fuelSchema,
    color: z.string().nullable(),
    origin: originSchema.nullable(),
    usage: usageSchema.nullable(),
    odometerKm: z.number().int().nullable(),
    ownerCount: z.number().int().nullable(),
    registrationExpiry: isoDateSchema.nullable(),
    firstRegistrationDate: isoDateSchema.nullable(),
    referencePrice: amountSchema.nullable(),
    referenceSource: z.string().nullable(),
    referenceDate: isoDateSchema.nullable(),
    valuationDate: isoDateSchema.nullable(),
    /** `null` = nobody has answered, which is never read as "No" (PP §7.1). */
    faceliftApplies: z.boolean().nullable(),
    customsApplies: z.boolean().nullable(),
    hasSpareKey: z.boolean(),
    hasOwnersManual: z.boolean(),
    hasToolKit: z.boolean(),
    hasSpareTire: z.boolean(),
    hasWarranty: z.boolean(),
    serviceHistory: serviceHistorySchema,
    equipment: z.array(z.string()),
    acquisitionType: acquisitionTypeSchema,
    profitMode: profitModeSchema,
    paramOverrides: paramOverridesSchema,
    taxTrafficEstimate: amountSchema.nullable(),
    otherDirectCostEstimate: amountSchema.nullable(),
    /**
     * Registration and ownership-transfer fees typed for this vehicle, in JOD. `null` means "use the
     * template": the `registration` and `transfer` equations price them. A value replaces both figures
     * (business review 2026-09-23); a recorded cost item in either category still wins over it.
     */
    registrationTransferFee: amountSchema.nullable(),
});
/** The inspection as the estimator records it (plan §4 `inspections`). */
export const inspectionSchema = z.object({
    id: uuidSchema.nullable(),
    grade7: grade7Schema,
    interior: severitySchema,
    engine: severitySchema,
    hybrid: severitySchema,
    tires: severitySchema,
    /** Index into the template version's seven-row damage table; `null` = nothing selected. */
    damageGroup1Index: z.number().int().min(0).max(6).nullable(),
    damageGroup2Index: z.number().int().min(0).max(6).nullable(),
    inspectorNotes: z.string().nullable(),
    inspectedAt: z.string().datetime().nullable(),
    inspectedBy: uuidSchema.nullable(),
});
/** One of the four condition × mileage benchmarks of the quick range (plan §5.11). */
export const quickRangeScenarioSchema = z.object({
    key: quickCategorySchema,
    condition: z.enum(['GOOD', 'POOR']),
    mileage: z.enum(['LOW', 'HIGH']),
    km: z.number(),
    appraisal: z.number().nullable(),
});
export const mileageBandSchema = z.enum(['LOW', 'HIGH']);
/**
 * A question the quick range answered on the salesperson's behalf. Plan §5.11: "inclusions and
 * facelift/customs **as entered (unknown → not applied, labelled)**" — the early benchmark is allowed
 * to assume, as long as it says so. The detailed appraisal never assumes; there, unanswered stays
 * missing and the estimate stays a draft.
 */
export const quickRangeAssumptionSchema = z.enum(['FACELIFT', 'CUSTOMS']);
/**
 * The stored quick range (`estimates.quick_range`). It records **which versions produced it**, so a
 * range shown next to a figure can always be traced to the policy and template values behind it.
 */
export const quickRangeSchema = z.object({
    computedAt: z.string().datetime().nullable(),
    policyVersionId: uuidSchema.nullable(),
    templateVersionId: uuidSchema.nullable(),
    expectedKm: z.number().nullable(),
    band: mileageBandSchema.nullable(),
    lowKm: z.number().nullable(),
    highKm: z.number().nullable(),
    min: z.number().nullable(),
    max: z.number().nullable(),
    /** True when the identity facts are not complete enough to price anything yet. */
    incomplete: z.boolean(),
    /** What the range assumed because nobody had answered yet; printed under the scenarios. */
    assumptions: z.array(quickRangeAssumptionSchema),
    scenarios: z.array(quickRangeScenarioSchema),
});
/** A fact the valuation still needs; the estimator lists these as "incomplete" (plan §11.10). */
export const missingInputSchema = z.object({
    name: z.string(),
    field: z.string(),
    detail: z.string(),
});
/** A value that is *wrong* rather than absent; these block rather than being clipped (PP §7.5). */
export const blockingIssueSchema = z.object({
    code: z.string(),
    field: z.string(),
    detail: z.string(),
});
/**
 * The valuation summary carried on the estimate detail so one round trip answers "what is this vehicle
 * worth and what is still missing". The full inputs, provenance and trace live on `valuations.compute`.
 */
export const estimateValuationSchema = z.object({
    outputs: z.record(z.string(), z.number().nullable()),
    missing: z.array(missingInputSchema),
    blocking: z.array(blockingIssueSchema),
    /** Any figure that still contains an estimate rather than an actual (plan §5.4). */
    provisional: z.boolean(),
    /** A negative ceiling: a legitimate "there is no economic offer here" finding, not an error. */
    noEconomicOffer: z.boolean(),
    issues: z.array(z.string()),
    fingerprint: z.string(),
    policyVersionId: uuidSchema.nullable(),
    templateVersionId: uuidSchema.nullable(),
    valuationDate: isoDateSchema,
});
export const contactSummarySchema = z.object({
    id: uuidSchema,
    name: z.string(),
    phone: z.string(),
});
export const templateRefSchema = z.object({
    templateId: uuidSchema,
    name: z.string(),
    versionId: uuidSchema,
    version: z.number().int().min(1),
});
export const policyRefSchema = z.object({
    versionId: uuidSchema,
    version: z.number().int().min(1),
});
/**
 * The stock vehicle an estimate became, as the offer step links to it. Deliberately three fields and
 * declared here rather than imported from the vehicles area: the estimate only ever needs to say "this
 * became `UC-000201`, and it is in the warehouse".
 */
export const convertedVehicleSchema = z.object({
    id: uuidSchema,
    stockNo: z.string(),
    status: inventoryStatusSchema,
});
/** A saved snapshot summary. Snapshots themselves arrive in phase 4; this stays `null` until then. */
export const valuationSnapshotSummarySchema = z.object({
    id: uuidSchema,
    seq: z.number().int().min(1),
    valuationDate: isoDateSchema,
    appraisal: z.number().nullable(),
    ceiling: z.number().nullable(),
    requiredRetail: z.number().nullable(),
    fingerprint: z.string(),
    /** True when the record has moved on since the snapshot was taken (plan §5.2). */
    isStale: z.boolean(),
    createdAt: z.string().datetime(),
});
export const estimateSummarySchema = z.object({
    id: uuidSchema,
    estimateNo: z.string(),
    status: estimateStatusSchema,
    customerName: z.string().nullable(),
    customerPhone: z.string().nullable(),
    customerAskingPrice: amountSchema.nullable(),
    ctaOffer: amountSchema.nullable(),
    make: z.string().nullable(),
    model: z.string().nullable(),
    modelYear: z.number().int().nullable(),
    odometerKm: z.number().int().nullable(),
    vin: z.string().nullable(),
    templateId: uuidSchema.nullable(),
    templateName: z.string().nullable(),
    assignedUserId: uuidSchema.nullable(),
    assignedUserName: z.string().nullable(),
    quickCategory: quickCategorySchema.nullable(),
    convertedVehicleId: uuidSchema.nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const estimateDetailSchema = vehicleFactsSchema.extend({
    id: uuidSchema,
    estimateNo: z.string(),
    status: estimateStatusSchema,
    revision: revisionSchema,
    customerContactId: uuidSchema.nullable(),
    customer: contactSummarySchema.nullable(),
    customerAskingPrice: amountSchema.nullable(),
    /** Present and nullable from phase 3; only phase 4's `shareOffer` ever writes it. */
    ctaOffer: amountSchema.nullable(),
    offerSharedAt: z.string().datetime().nullable(),
    outcomeNote: z.string().nullable(),
    closedAt: z.string().datetime().nullable(),
    reopenedAt: z.string().datetime().nullable(),
    template: templateRefSchema.nullable(),
    policy: policyRefSchema.nullable(),
    inspection: inspectionSchema.nullable(),
    quickCategory: quickCategorySchema.nullable(),
    quickRange: quickRangeSchema.nullable(),
    valuation: estimateValuationSchema,
    latestSnapshot: valuationSnapshotSummarySchema.nullable(),
    /** The fingerprint of the estimate *as it is now* (plan §5.2). */
    currentFingerprint: z.string(),
    /** The live above-ceiling request, with `isStale` recomputed against the current fingerprint. */
    approval: approvalSummarySchema.nullable(),
    convertedVehicleId: uuidSchema.nullable(),
    convertedVehicle: convertedVehicleSchema.nullable(),
    costItems: costItemsSummarySchema,
    /**
     * The purchase rule's own answer (plan §5.6), so the button can be honest instead of optimistic: the
     * blockers are reason keys the UI translates and lists under a disabled "Customer accepts".
     */
    canPurchase: z.boolean(),
    purchaseBlockers: z.array(z.string()),
    assignedUserId: uuidSchema.nullable(),
    assignedUserName: z.string().nullable(),
    createdBy: uuidSchema.nullable(),
    activityCount: z.number().int().min(0),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const listEstimatesSortBySchema = z.enum(['updatedAt', 'createdAt', 'estimateNo', 'customerAskingPrice', 'ctaOffer']);
export const listEstimatesInputSchema = paginationInputSchema.extend({
    sortBy: listEstimatesSortBySchema.default('updatedAt'),
    sortDirection: sortDirectionSchema.default('desc'),
    /** Free text over the estimate number, the customer's name and phone, make, model and VIN. */
    q: z.string().trim().min(1).optional(),
    /** Absent means **every** status — an estimate is searchable for ever (plan §3.1). */
    status: z.array(estimateStatusSchema).optional(),
    templateId: uuidSchema.optional(),
    assignedUserId: uuidSchema.optional(),
    updatedFrom: isoDateSchema.optional(),
    updatedTo: isoDateSchema.optional(),
});
export const listEstimatesOutputSchema = paginatedSchema(estimateSummarySchema);
export const getEstimateInputSchema = z.object({
    estimateId: uuidSchema,
});
/**
 * Creating an estimate allocates `EST-<year>-NNNN` from the tenant counter and applies the template's
 * published version: the version and policy references, the make/model/fuel/origin defaults and the
 * reference price/source/date. The **condition starts UNKNOWN** and is never pre-filled (PP AC03).
 */
export const createEstimateInputSchema = z.object({
    templateId: uuidSchema.optional(),
});
/**
 * Name + mobile; the service finds or creates the contact by normalised phone (plan §10).
 *
 * `matchExisting: false` is the salesperson's answer to "Did you mean <name>?" — *no, keep what I
 * typed*: the service then links a contact with this phone **and this name**, or creates one, instead
 * of adopting whoever already owns the number (business review 2026-09-23). Absent means `true`.
 */
export const inlineCustomerSchema = z.object({
    name: z.string().trim().min(1, 'Enter the customer’s name').max(255),
    phone: z.string().trim().min(3, 'Enter a mobile number').max(32),
    matchExisting: z.boolean().optional(),
});
export const updateEstimateInputSchema = z.object({
    estimateId: uuidSchema,
    expectedRevision: revisionSchema,
    customerContactId: uuidSchema.nullish(),
    customer: inlineCustomerSchema.optional(),
    customerAskingPrice: amountSchema.nullish(),
    make: z.string().trim().max(80).nullish(),
    model: z.string().trim().max(80).nullish(),
    trim: z.string().trim().max(80).nullish(),
    modelYear: modelYearSchema.nullish(),
    vin: vinSchema.nullish(),
    plate: z.string().trim().max(32).nullish(),
    drivetrain: z.string().trim().max(32).nullish(),
    transmission: z.string().trim().max(32).nullish(),
    fuel: fuelSchema.optional(),
    color: z.string().trim().max(40).nullish(),
    origin: originSchema.nullish(),
    usage: usageSchema.nullish(),
    odometerKm: odometerSchema.nullish(),
    ownerCount: ownerCountSchema.nullish(),
    registrationExpiry: isoDateSchema.nullish(),
    firstRegistrationDate: isoDateSchema.nullish(),
    referencePrice: amountSchema.nullish(),
    referenceSource: z.string().trim().max(255).nullish(),
    referenceDate: isoDateSchema.nullish(),
    valuationDate: isoDateSchema.nullish(),
    faceliftApplies: z.boolean().nullish(),
    customsApplies: z.boolean().nullish(),
    hasSpareKey: z.boolean().optional(),
    hasOwnersManual: z.boolean().optional(),
    hasToolKit: z.boolean().optional(),
    hasSpareTire: z.boolean().optional(),
    hasWarranty: z.boolean().optional(),
    serviceHistory: serviceHistorySchema.optional(),
    equipment: z.array(z.string().trim().min(1).max(120)).max(100).optional(),
    acquisitionType: acquisitionTypeSchema.optional(),
    profitMode: profitModeSchema.optional(),
    /** Only keys the template version marks overridable are accepted; anything else is a 400. */
    paramOverrides: paramOverridesInputSchema.optional(),
    taxTrafficEstimate: amountSchema.nullish(),
    otherDirectCostEstimate: amountSchema.nullish(),
    /** `null` clears it and the template's registration and transfer figures apply again. */
    registrationTransferFee: amountSchema.nullish(),
    assignedUserId: uuidSchema.nullish(),
});
/** One field applying a template would change, old → new (PP AC09). */
export const templateChangeSchema = z.object({
    field: z.string(),
    from: z.union([z.string(), z.number(), z.null()]),
    to: z.union([z.string(), z.number(), z.null()]),
});
export const applyTemplatePreviewInputSchema = z.object({
    estimateId: uuidSchema,
    templateId: uuidSchema,
});
export const applyTemplatePreviewOutputSchema = z.object({
    estimateId: uuidSchema,
    templateId: uuidSchema,
    templateName: z.string(),
    versionId: uuidSchema,
    version: z.number().int().min(1),
    changes: z.array(templateChangeSchema),
    /** Overrides dropped because the new version no longer allows that key. */
    clearedOverrides: z.array(z.string()),
});
export const applyTemplateInputSchema = z.object({
    estimateId: uuidSchema,
    expectedRevision: revisionSchema,
    templateId: uuidSchema,
});
export const quickRangeInputSchema = z.object({
    estimateId: uuidSchema,
});
export const selectQuickCategoryInputSchema = z.object({
    estimateId: uuidSchema,
    expectedRevision: revisionSchema,
    /** `null` clears the early indicator. */
    quickCategory: quickCategorySchema.nullable(),
});
export const listEstimateActivityInputSchema = paginationInputSchema.extend({
    estimateId: uuidSchema,
});
export const activityChangeSchema = z.object({
    field: z.string(),
    from: z.union([z.string(), z.number(), z.boolean(), z.null()]),
    to: z.union([z.string(), z.number(), z.boolean(), z.null()]),
});
export const activityEventSchema = z.object({
    id: uuidSchema,
    kind: activityKindSchema,
    fromStatus: z.string().nullable(),
    toStatus: z.string().nullable(),
    changes: z.array(activityChangeSchema),
    reason: z.string().nullable(),
    actorId: uuidSchema.nullable(),
    actorName: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const listEstimateActivityOutputSchema = paginatedSchema(activityEventSchema);
/**
 * Recording the offer (plan §5.6). `ctaOffer` is optional because the prototype's button defaults it to
 * the calculated ceiling — a salesperson who presses "Share / record offer" without typing anything has
 * offered the recommended number, and that is what the customer will be told.
 *
 * An offer **above** the ceiling may be recorded; it simply cannot be *purchased* without an
 * administrator's approval, which is what `aboveCeiling` on the response tells the page to ask for.
 *
 * `positive`, like `recordSaleSchema.salePrice`: an offer of zero is not an offer. A nonnegative one was
 * accepted, flipped the estimate to `OFFER_SHARED`, wrote a snapshot and an activity row, and was only
 * caught at the purchase — where `purchasePriceInvalid` names a field nobody had typed.
 */
export const shareOfferInputSchema = z.object({
    estimateId: uuidSchema,
    expectedRevision: revisionSchema,
    ctaOffer: amountSchema.positive().optional(),
});
export const shareOfferOutputSchema = z.object({
    estimate: estimateDetailSchema,
    ctaOffer: amountSchema.nullable(),
    ceiling: z.number().nullable(),
    /** The recorded offer is higher than the calculated ceiling — a warning, not a refusal. */
    aboveCeiling: z.boolean(),
    /** `offer − ceiling`, so the page does not have to subtract two nullable numbers itself. */
    overCeilingBy: signedMoneySchema.nullable(),
});
export const closeNoDealInputSchema = z.object({
    estimateId: uuidSchema,
    expectedRevision: revisionSchema,
    reason: z.string().trim().min(1, 'Say why the deal did not happen').max(2_000),
});
/** ADMIN only, and `NO_DEAL` only: `DEAL_AGREED` is never reopened, because the vehicle exists. */
export const reopenEstimateInputSchema = z.object({
    estimateId: uuidSchema,
    expectedRevision: revisionSchema,
    reason: z.string().trim().max(2_000).optional(),
});
/**
 * Confirming the purchase — **the only way a stock vehicle is ever created** (plan §3.1, §5.6).
 *
 * Everything the rule checks is either here or already on the record: the price actually agreed, the day
 * it was agreed (never later than today in the tenant's calendar), where the vehicle will stand, and
 * whether it goes straight into the workshop.
 */
export const purchaseEstimateInputSchema = z.object({
    estimateId: uuidSchema,
    expectedRevision: revisionSchema,
    purchasePrice: amountSchema,
    purchaseDate: isoDateSchema,
    initialStatus: z.enum(['IN_WAREHOUSE', 'IN_SERVICE']),
    storageLocation: z.string().trim().min(1, 'Say where the vehicle will stand').max(200),
});
export const checkVinInputSchema = z.object({
    vin: z.string().trim().min(1).max(64),
    excludeEstimateId: uuidSchema.optional(),
});
export const vinMatchSchema = z.object({
    id: uuidSchema,
    estimateNo: z.string(),
    status: estimateStatusSchema,
    customerName: z.string().nullable(),
    make: z.string().nullable(),
    model: z.string().nullable(),
    modelYear: z.number().int().nullable(),
    updatedAt: z.string().datetime(),
});
/**
 * Informational in phase 3: a VIN already on another **open** estimate is worth telling the salesperson
 * about, but nothing is blocked. Stock vehicles do not exist until phase 4, so `vehicles` is empty.
 */
export const checkVinOutputSchema = z.object({
    vin: z.string(),
    vinNormalized: z.string(),
    estimates: z.array(vinMatchSchema),
    vehicles: z.array(z.object({ id: uuidSchema, stockNo: z.string(), status: z.string() })),
});
//# sourceMappingURL=estimates.schemas.js.map